import { Add, Delete, Edit } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DataGridPro, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro'
import type { Contact } from '@otw/models/leads/contacts'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import {
  buildAudienceEvalContext,
  previewAudienceEligibility,
  summarizeAudienceFilter,
  type MarketingAudience,
  type MarketingCampaign,
} from '@otw/models/leads/marketingCampaign'
import { getTerminalDisplayName, type Terminal } from '@otw/models/leads/terminals'
import type { PickupLocation, PrivateUserInfoType } from '@otw/models'
import React, { useMemo, useState } from 'react'
import { useMarketingAudiences } from 'hooks/contacts/useMarketingAudiences'
import { getUserDisplayName } from 'hooks/useUsers'
import { formatNumber } from '../../util/customerUtils'
import AudienceFormDialog from './AudienceFormDialog'

interface AudiencesPanelProps {
  campaigns: MarketingCampaign[]
  contacts: Contact[]
  companies: ContactCompany[]
  pickupLocations: PickupLocation[]
  terminals: Terminal[]
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
}

interface AudienceRow {
  id: string
  audience: MarketingAudience
  name: string
  rules: string
  matchingCount: number
  eligibleCount: number
  usedByCount: number
  updatedAt: Date
}

const AudiencesPanel: React.FC<AudiencesPanelProps> = ({
  campaigns,
  contacts,
  companies,
  pickupLocations,
  terminals,
  salesOwners,
  inventoryOwners,
}) => {
  const theme = useTheme()
  const { audiences, isLoading, deleteAudience, isSaving } = useMarketingAudiences()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingAudience | null>(null)
  const [pendingDelete, setPendingDelete] = useState<MarketingAudience | null>(null)

  const evalContext = useMemo(
    () => buildAudienceEvalContext({ companies, pickupLocations }),
    [companies, pickupLocations],
  )

  const usedByCountById = useMemo(() => {
    const counts = new Map<string, number>()
    for (const campaign of campaigns) {
      const audienceId = campaign.savedAudienceId
      if (!audienceId) continue
      counts.set(audienceId, (counts.get(audienceId) ?? 0) + 1)
    }
    return counts
  }, [campaigns])

  const filterLabels = useMemo(() => {
    const ownerNameById = new Map<string, string>()
    for (const user of [...salesOwners, ...inventoryOwners]) {
      ownerNameById.set(String(user.id), getUserDisplayName(user))
    }
    const terminalNameById = new Map<string, string>()
    for (const terminal of terminals) {
      terminalNameById.set(terminal.id, getTerminalDisplayName(terminal))
    }
    return { ownerNameById, terminalNameById }
  }, [inventoryOwners, salesOwners, terminals])

  const rows = useMemo<AudienceRow[]>(
    () =>
      audiences.map((audience) => {
        const preview = previewAudienceEligibility(contacts, audience.filter, evalContext, {
          includedContactIds: audience.includedContactIds,
          excludedContactIds: audience.excludedContactIds,
        })
        return {
          id: audience.id,
          audience,
          name: audience.name || 'Untitled audience',
          rules: [
            summarizeAudienceFilter(audience.filter, filterLabels),
            (audience.includedContactIds?.length ?? 0) > 0
              ? `${formatNumber(audience.includedContactIds?.length ?? 0)} handpicked`
              : '',
            (audience.excludedContactIds?.length ?? 0) > 0
              ? `${formatNumber(audience.excludedContactIds?.length ?? 0)} excluded`
              : '',
          ]
            .filter(Boolean)
            .join(' · '),
          matchingCount: preview.matchingCount,
          eligibleCount: preview.eligibleCount,
          usedByCount: usedByCountById.get(audience.id) ?? 0,
          updatedAt: audience.updatedAt,
        }
      }),
    [audiences, contacts, evalContext, filterLabels, usedByCountById],
  )

  const columns = useMemo<GridColDef<AudienceRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Audience',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              minWidth: 0,
            }}
          >
            <Typography variant="body2" fontWeight={700} noWrap>
              {params.row.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'rules',
        headerName: 'Rules',
        flex: 2,
        minWidth: 260,
        renderCell: (params) => (
          <Tooltip title={params.row.rules}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minWidth: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary" noWrap>
                {params.row.rules}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'matchingCount',
        headerName: 'Matching',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatNumber(Number(value) || 0),
      },
      {
        field: 'eligibleCount',
        headerName: 'Eligible',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
              height: '100%',
            }}
          >
            <Chip
              size="small"
              color={params.row.eligibleCount > 0 ? 'success' : 'default'}
              variant="outlined"
              label={formatNumber(params.row.eligibleCount)}
            />
          </Box>
        ),
      },
      {
        field: 'usedByCount',
        headerName: 'Used by',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => {
          const count = Number(value) || 0
          return count === 0 ? '—' : `${formatNumber(count)} campaign${count === 1 ? '' : 's'}`
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 170,
        valueFormatter: (value: Date | null) =>
          value instanceof Date && !Number.isNaN(value.getTime())
            ? value.toLocaleString('en-US')
            : '—',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 96,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit fontSize="small" />}
            label="Edit"
            disabled={isSaving}
            onClick={() => {
              setEditing(params.row.audience)
              setFormOpen(true)
            }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete fontSize="small" />}
            label="Delete"
            disabled={isSaving}
            onClick={() => setPendingDelete(params.row.audience)}
          />,
        ],
      },
    ],
    [isSaving],
  )

  const handleDelete = async () => {
    if (!pendingDelete) return
    await deleteAudience(pendingDelete.id)
    setPendingDelete(null)
  }

  const pendingDeleteUsedBy = pendingDelete
    ? usedByCountById.get(pendingDelete.id) ?? 0
    : 0

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
          A saved audience is a named set of rules, not a frozen list. Counts below are live
          and recipients are re-checked again at send.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditing(null)
            setFormOpen(true)
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          New audience
        </Button>
      </Stack>

      {isLoading ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 6 }}>
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        </Paper>
      ) : rows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.background.default, 0.6),
          }}
        >
          <Typography variant="h6" gutterBottom>
            No saved audiences yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save a set of rules once, then reuse it on any campaign.
          </Typography>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{ height: '62vh', borderRadius: 3, overflow: 'hidden' }}
        >
          <DataGridPro<AudienceRow>
            rows={rows}
            columns={columns}
            density="comfortable"
            disableRowSelectionOnClick
            hideFooter
            initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
            onRowClick={(params) => {
              setEditing(params.row.audience)
              setFormOpen(true)
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
          />
        </Paper>
      )}

      <AudienceFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        audience={editing}
        contacts={contacts}
        companies={companies}
        pickupLocations={pickupLocations}
        terminals={terminals}
        salesOwners={salesOwners}
        inventoryOwners={inventoryOwners}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={isSaving ? undefined : () => setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Delete color="error" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Delete this audience?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong>{pendingDelete?.name || 'Untitled audience'}</strong> will be removed
              from the audience list.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingDeleteUsedBy > 0
                ? `${formatNumber(pendingDeleteUsedBy)} campaign${pendingDeleteUsedBy === 1 ? '' : 's'} reference this audience. They keep their own copy of the rules and are not deleted.`
                : 'No campaigns reference this audience. No campaigns are deleted.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setPendingDelete(null)}
            color="inherit"
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDelete()}
            disabled={isSaving}
          >
            {isSaving ? 'Deleting…' : 'Delete audience'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AudiencesPanel
